import React from 'react';
import { AutoComplete as $AutoComplete, Input as $Input } from 'antd';
import { AutoCompleteProps as $AutoCompleteProps } from 'antd/lib/auto-complete';
import { FilterFunc, DefaultOptionType } from 'rc-select/lib/Select';

import { OptionProps, OnBlurCallback, OnChangeCallback } from '.';

export type AutoCompleteProps = Omit<$AutoCompleteProps, 'onBlur' | 'onChange'> & {
  children?: React.ReactNode;
} & {
  name?: string;
  options?: OptionProps[];
  maxLength?: number;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
  onSelect?: OnChangeCallback;
  filterOption?: FilterFunc<DefaultOptionType>;
  allowFreeTextInput?: boolean;
};

const defaultFilterOption = (input: string, option: DefaultOptionType | undefined) =>
  (option?.label as string)?.toLocaleLowerCase()?.includes(input.toLocaleLowerCase());

const AutoComplete = React.memo(function AutoComplete({
  name,
  options,
  children,
  maxLength,
  onBlur,
  onChange,
  onSelect,
  filterOption,
  value,
  allowFreeTextInput,
  ...rest
}: AutoCompleteProps) {
  const handleBlur =
    onBlur &&
    ((value: any) => {
      onBlur({ name, value });
    });
  const handleChange = (value: any) => {
    if (onChange) {
      onChange({ name, value: value || undefined });
    }
  };

  const handleSelect = (value: any) => {
    if (onSelect) {
      onSelect({ name, value });
    }
  };

  // antd v4 changed how the input value is displayed
  // It will no longer display the "label" but instead displays the "value" of the selected options
  const displayValue = !options?.length ? value : options.find((opt) => opt.value === value)?.label;

  return (
    <$AutoComplete
      {...rest}
      value={allowFreeTextInput ? value : displayValue}
      options={options}
      filterOption={filterOption ?? defaultFilterOption}
      onBlur={handleBlur}
      onChange={handleChange}
      onSelect={handleSelect}
      getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
    >
      {children || <$Input maxLength={maxLength} />}
    </$AutoComplete>
  );
});

export default AutoComplete;
